import { useEffect, useState } from "react";
import { Trash2, X, Mail, Phone, MapPin, Calendar, Package, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
    pending: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    fulfilled: "bg-blue-100 text-blue-700",
    cancelled: "bg-gray-200 text-gray-600",
};

const STATUSES = ["pending", "paid", "fulfilled", "cancelled"];

const PAYMENT_STATUS_COLORS = {
    unpaid: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    refunded: "bg-gray-200 text-gray-600",
};

const PAYMENT_STATUSES = ["unpaid", "paid", "refunded"];

function stripeDashboardUrl(paymentRef) {
    if (!paymentRef) return null;
    if (paymentRef.startsWith("pi_")) return `https://dashboard.stripe.com/payments/${paymentRef}`;
    if (paymentRef.startsWith("cs_")) return `https://dashboard.stripe.com/checkout/sessions/${paymentRef}`;
    return null;
}

export default function AdminOrders() {
    const [items, setItems] = useState([]);
    const [viewing, setViewing] = useState(null);

    const load = () => api.get("/orders").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.put(`/orders/${id}/status?status=${status}`);
            toast.success("Status updated");
            load();
            if (viewing && viewing.id === id) setViewing({ ...viewing, status });
        } catch { toast.error("Update failed"); }
    };

    const updatePaymentStatus = async (id, payment_status) => {
        try {
            await api.put(`/orders/${id}/payment-status?payment_status=${payment_status}`);
            toast.success("Payment status updated");
            load();
            if (viewing && viewing.id === id) setViewing({ ...viewing, payment_status });
        } catch { toast.error("Update failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this order?")) return;
        try { await api.delete(`/orders/${id}`); toast.success("Deleted"); load(); setViewing(null); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="admin-orders">
            <div className="mb-6">
                <h2 className="font-display text-3xl">Orders</h2>
                <p className="text-sm text-ink-soft mt-1">All online shop orders. Status updates show in customer-facing communication.</p>
            </div>

            <div className="space-y-2">
                {items.map((o, i) => (
                    <button
                        key={o.id}
                        onClick={() => setViewing(o)}
                        className="w-full text-left bg-white border border-[#EAE3DE] p-4 flex items-center gap-4 hover:border-blush transition-colors"
                        data-testid={`order-row-${i}`}
                    >
                        <Package size={20} className="text-blush" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-display text-lg">{o.order_number}</p>
                                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 ${
                                    o.customer_id ? "bg-emerald-100 text-emerald-700" : "bg-blush-pale text-blush"
                                }`}>
                                    {o.customer_id ? "Registered" : "Guest"}
                                </span>
                            </div>
                            <p className="text-xs text-ink-soft">
                                {o.customer_name} · {new Date(o.created_at).toLocaleDateString("en-AU")} · ${o.total.toFixed(2)} AUD
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${STATUS_COLORS[o.status] || "bg-gray-100"}`}>
                                {o.status}
                            </span>
                            <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 ${PAYMENT_STATUS_COLORS[o.payment_status] || "bg-gray-100"}`}>
                                {o.payment_status || "unpaid"}
                            </span>
                        </div>
                    </button>
                ))}
                {items.length === 0 && <p className="text-center text-ink-soft py-12">No orders yet.</p>}
            </div>

            {viewing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="font-display text-2xl">{viewing.order_number}</h3>
                                <p className="text-sm text-ink-soft">{new Date(viewing.created_at).toLocaleString("en-AU")}</p>
                            </div>
                            <button onClick={() => setViewing(null)}><X size={20} /></button>
                        </div>

                        <div className="space-y-5">
                            <section>
                                <h4 className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Customer</h4>
                                <p className="font-medium">{viewing.customer_name}</p>
                                <p className="text-sm flex items-center gap-2 mt-1"><Mail size={12} /> {viewing.customer_email}</p>
                                <p className="text-sm flex items-center gap-2 mt-1"><Phone size={12} /> {viewing.customer_phone}</p>
                                <p className="text-sm flex items-center gap-2 mt-1">
                                    <MapPin size={12} />
                                    <span className="text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-[#EAE3DE] text-ink shrink-0">
                                        {viewing.fulfillment_type === "pickup" ? "Pickup" : "Delivery"}
                                    </span>
                                    {viewing.delivery_address}
                                </p>
                                {viewing.delivery_date && <p className="text-sm flex items-center gap-2 mt-1"><Calendar size={12} /> {viewing.delivery_date}</p>}
                                {viewing.cake_message && (
                                    <p className="text-sm mt-2"><span className="text-ink-soft">Cake Message:</span> "{viewing.cake_message}"</p>
                                )}
                                {viewing.colour_notes && (
                                    <p className="text-sm mt-1"><span className="text-ink-soft">Colour/Design Notes:</span> {viewing.colour_notes}</p>
                                )}
                                {viewing.base_colour && (
                                    <p className="text-sm mt-1"><span className="text-ink-soft">Base Colour:</span> {viewing.base_colour}</p>
                                )}
                                {viewing.piped_colour && (
                                    <p className="text-sm mt-1"><span className="text-ink-soft">Piped Colour:</span> {viewing.piped_colour}</p>
                                )}
                            </section>

                            <section>
                                <h4 className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Items</h4>
                                <div className="space-y-2">
                                    {viewing.items.map((it, i) => (
                                        <div key={i} className="flex justify-between text-sm border-b border-[#EAE3DE] pb-2">
                                            <span>{it.name} × {it.qty}{it.option ? ` (${it.option})` : ""}</span>
                                            <span>${(it.price * it.qty).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between font-display text-lg mt-3">
                                    <span>Total</span>
                                    <span>${viewing.total.toFixed(2)} AUD</span>
                                </div>
                            </section>

                            <section>
                                <h4 className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Payment</h4>
                                <p className="text-sm capitalize">{viewing.payment_method}</p>
                                {viewing.payment_ref && (
                                    <p className="text-xs text-ink-soft mt-1 flex items-center gap-1">
                                        Ref: {viewing.payment_ref}
                                        {stripeDashboardUrl(viewing.payment_ref) && (
                                            <a
                                                href={stripeDashboardUrl(viewing.payment_ref)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blush hover:underline inline-flex items-center gap-0.5"
                                            >
                                                View in Stripe <ExternalLink size={10} />
                                            </a>
                                        )}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {PAYMENT_STATUSES.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => updatePaymentStatus(viewing.id, s)}
                                            className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${
                                                (viewing.payment_status || "unpaid") === s ? "bg-blush text-white border-blush" : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                            }`}
                                            data-testid={`payment-status-${s}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </section>

                            {viewing.notes && (
                                <section>
                                    <h4 className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Notes</h4>
                                    <p className="text-sm">{viewing.notes}</p>
                                </section>
                            )}

                            <section>
                                <h4 className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Status</h4>
                                <div className="flex flex-wrap gap-2">
                                    {STATUSES.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => updateStatus(viewing.id, s)}
                                            className={`px-3 py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${
                                                viewing.status === s ? "bg-blush text-white border-blush" : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </section>

                            <button
                                onClick={() => remove(viewing.id)}
                                className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                            >
                                <Trash2 size={12} /> Delete order
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
